import { ChangeDetectionStrategy, Component, inject, Renderer2 } from '@angular/core';
import { RouterModule } from '@angular/router';
import { DfModuleImageModule } from '@df/module-image/df-module-image.module';
import { BgColorOptions } from '@df/ui/atomic/atomic-options';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { ButtonStyleComponent } from '../../common/buttons/button-style/button-style.component';
import { NTextBox, TextBoxComponent } from '../../common/text-box/text-box.component';
import { defaultImage } from '../../icms.config';

export namespace NCmsMosaic {
    export const ID = 'mosaic';
    export interface IData extends NICmsContentComponent.IData, NTextBox.IData {
        rows: IRow[];
    }

    export interface IRow {
        variant: 'fullWidth' | 'columns' | 'columns-reverse';
        backgroundColor: 'bg-col-51' | 'bg-col-52' | 'bg-col-w';
        image: string;
        link: string;
        title: string;
        buttonLabel: string;
        image2?: string;
        link2?: string;
        title2?: string;
        buttonLabel2?: string;
    }
}
@ICmsComponent<CmsMosaicComponent>({
    id: NCmsMosaic.ID,
    name: 'Mosaic',
    tags: ['Image', 'Homepage'],
    fields: [
        ...NTextBox.getFields(false, false),
        <NICmsContentField.IRepeaterField>{
            key: 'rows',
            type: NICmsContentField.EFieldType.repeater,
            title: 'Rows',
            itemName: 'Row',
            minSize: 1,
            defaultSize: 2,
            complex: true,
            fields: [
                <NICmsContentField.IChoiceField>{
                    key: 'variant',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Variant',
                    options: [
                        { key: 'fullWidth', title: 'Full width' },
                        { key: 'columns', title: '2 Images' },
                        { key: 'columns-reverse', title: '2 Images alternative' }
                    ],
                    default: 'columns'
                },
                <NICmsContentField.IChoiceField>{
                    key: 'backgroundColor',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Background Color',
                    previewMode: 'color',
                    options: BgColorOptions.filter(option => ['bg-col-51', 'bg-col-52', 'bg-col-w'].includes(String(option.key))),
                    default: 'bg-col-51'
                },
                {
                    key: 'imageLabel',
                    type: NICmsContentField.EFieldType.label,
                    title: 'First Image',
                    canShow: (data: NCmsMosaic.IRow) => data.variant !== 'fullWidth'
                },
                {
                    key: 'link',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Link',
                    description: 'Link will be applied to the whole element',
                    default: '/',
                    required: true
                },
                {
                    key: 'image',
                    type: NICmsContentField.EFieldType.image,
                    title: 'Image',
                    default: defaultImage,
                    required: true
                },
                {
                    key: 'title',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Title',
                    default: 'Title goes here',
                    required: true
                },
                {
                    key: 'buttonLabel',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Button label',
                    default: 'Button label',
                    required: true
                },
                {
                    key: 'imageLabel2',
                    type: NICmsContentField.EFieldType.label,
                    title: 'Second Image',
                    canShow: (data: NCmsMosaic.IRow) => data.variant !== 'fullWidth'
                },
                {
                    key: 'image2',
                    type: NICmsContentField.EFieldType.image,
                    title: 'Image',
                    default: defaultImage,
                    canShow: (data: NCmsMosaic.IRow) => data.variant !== 'fullWidth',
                    required: true
                },
                {
                    key: 'link2',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Link',
                    description: 'Link will be applied to the whole element',
                    default: '/',
                    canShow: (data: NCmsMosaic.IRow) => data.variant !== 'fullWidth',
                    required: true
                },
                {
                    key: 'title2',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Title',
                    default: 'Title goes here',
                    canShow: (data: NCmsMosaic.IRow) => data.variant !== 'fullWidth',
                    required: true
                },
                {
                    key: 'buttonLabel2',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Button Label',
                    default: 'Button label',
                    canShow: (data: NCmsMosaic.IRow) => data.variant !== 'fullWidth',
                    required: true
                }
            ]
        },
        NICmsContentComponent.backgroundField
    ]
})
@Component({
    imports: [TextBoxComponent, ButtonStyleComponent, ICmsClientModule, RouterModule, DfModuleImageModule],
    selector: 'cms-mosaic',
    templateUrl: './cms-mosaic.html',
    styleUrl: 'cms-mosaic.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CmsMosaicComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'component'
    })
    component!: NCmsMosaic.IData;

    private readonly renderer2 = inject(Renderer2);

    mouseEnterHandler(event): void {
        this.renderer2.addClass(event.target?.lastChild?.lastChild, 'active');
    }

    mouseLeaveHandler(event): void {
        this.renderer2.removeClass(event.target?.lastChild?.lastChild, 'active');
    }
}
